import React, { useEffect, useState } from 'react';

/**
 * Direction-A dimension input.
 *
 * A three-part control: minus-tick, monospace value input, plus-tick, all
 * living on a single hairline underline. No rounded pill buttons, no
 * gray-100 wells — fits the drafting-sheet grammar.
 */
export default function DimensionInput({
  id,
  label,
  hint,
  value,
  min,
  max,
  step = 10,
  suffix = 'mm',
  onCommit,
}: {
  id: string;
  label: string;
  hint?: string;
  value: number;
  min: number;
  max: number;
  step?: number;
  suffix?: string;
  onCommit: (next: number) => void;
}) {
  const [local, setLocal] = useState<string>(String(value));

  // Sync the local text back to the committed value if the store changes
  // externally (e.g. area clamp shortened the length).
  useEffect(() => {
    setLocal(String(value));
  }, [value]);

  const commit = (raw: string) => {
    const parsed = parseInt(raw, 10);
    if (Number.isNaN(parsed)) {
      setLocal(String(value));
      return;
    }
    const clamped = Math.max(min, Math.min(max, Math.round(parsed / step) * step));
    onCommit(clamped);
    setLocal(String(clamped));
  };

  return (
    <div className="grid grid-cols-1 sm:grid-cols-[minmax(0,1fr)_auto] items-start gap-x-6 gap-y-2 py-5 border-t border-ink/40 first:border-t-0">
      <label htmlFor={id} className="sv-field-label sm:pt-1">
        {label}
      </label>

      <div className="flex items-baseline gap-2">
        <button
          type="button"
          aria-label={`${label} verringern`}
          onClick={() => commit(String(value - step))}
          disabled={value - step < min}
          className="sv-mono text-[16px] text-ink hover:text-rubric disabled:text-ink-3/40 disabled:cursor-not-allowed w-5 text-center tabular-nums"
        >
          –
        </button>
        <input
          id={id}
          type="number"
          inputMode="numeric"
          min={min}
          max={max}
          step={step}
          value={local}
          onChange={(e) => setLocal(e.target.value)}
          onBlur={(e) => commit(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              commit((e.target as HTMLInputElement).value);
            }
          }}
          className="sv-dim-input sv-mono"
        />
        <button
          type="button"
          aria-label={`${label} erhöhen`}
          onClick={() => commit(String(value + step))}
          disabled={value + step > max}
          className="sv-mono text-[16px] text-ink hover:text-rubric disabled:text-ink-3/40 disabled:cursor-not-allowed w-5 text-center tabular-nums"
        >
          +
        </button>
        {suffix ? (
          <span className="sv-mono text-[16px] text-ink-3 min-w-[2rem]">{suffix}</span>
        ) : null}
      </div>
      {hint && <p className="sv-field-hint sm:col-span-2">{hint}</p>}
    </div>
  );
}
